import { clone } from '@jack-henry/frontend-utils/functions';
import '@vaadin/avatar';
import '@vaadin/button';
import '@vaadin/grid';
import '@vaadin/horizontal-layout';
import '@vaadin/icon';
import '@vaadin/icons';
import '@vaadin/select';
import '@vaadin/text-field';
import '@vaadin/vertical-layout';
import { html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { TmBaseComponent } from '../../tm-base.component';

@customElement('tm-pagination-controls')
export class TmPaginationControls extends TmBaseComponent {
    @property({ type: Number })
    public totalItemCount = 0;

    @property({ type: Number })
    public pageSize = 25;

    @property({ type: Number })
    public currentPage = 1;

    @property({ type: Array })
    public paginationOptions = ['10', '25', '50', '100'];

    @property()
    public itemLabel = 'items';

    @state()
    private pageCount = 1;

    private get pages() {
        return Array.from({ length: this.pageCount }, (_, i) => i + 1).map(pg => ({
            label: pg.toString(),
            value: pg.toString(),
        }));
    }

    public calculateOffset(): number {
        return (this.currentPage - 1) * this.pageSize;
    }

    // calculate index of the item at the beginning of the current page
    public calculateStartIndex(): number {
        return this.totalItemCount === 0 ? 0 : this.calculateOffset() + 1;
    }

    // calculate index of the item at the end of the current page
    public calculateEndIndex(): number {
        const endIndex = this.calculateOffset() + this.pageSize;
        return endIndex > this.totalItemCount ? this.totalItemCount : endIndex;
    }

    protected render() {
        if (this.totalItemCount === 0) {
            return nothing;
        }
        return html`
            <div class="flex justify-end items-center px-4">
                <span class="text-sm">
                    Viewing ${this.calculateStartIndex()} - ${this.calculateEndIndex()} of
                    ${this.totalItemCount}
                    ${this.pages.length === 1 ? this.renderPageSizeSelector() : nothing}
                </span>
                ${this.pages.length > 1
                    ? html`<div class="ml-2">
                          <vaadin-button
                              theme="small icon"
                              slot="end"
                              aria-label="Go to previous page"
                              ?disabled="${this.currentPage === 1}"
                              @click="${() => {
                                  this.currentPage -= 1;
                                  this.dispatchPageChanged();
                              }}"
                          >
                              <vaadin-icon icon="vaadin:angle-left"></vaadin-icon>
                          </vaadin-button>
                          <vaadin-select
                              class="w-16"
                              slot="end"
                              theme="small"
                              aria-labelledby="current-page-label"
                              style="--vaadin-input-field-value-font-size: var(--lumo-font-size-s);"
                              .items="${this.pages}"
                              .value="${this.currentPage.toString()}"
                              @value-changed="${(e: CustomEvent) => {
                                  this.currentPage = parseInt(e.detail.value);
                                  this.dispatchPageChanged();
                              }}"
                          ></vaadin-select>
                          <vaadin-button
                              theme="small icon"
                              slot="end"
                              aria-label="Go to next page"
                              ?disabled="${this.currentPage === this.pageCount}"
                              @click="${() => {
                                  this.currentPage += 1;
                                  this.dispatchPageChanged();
                              }}"
                          >
                              <vaadin-icon icon="vaadin:angle-right"></vaadin-icon>
                          </vaadin-button>
                          ${this.renderPageSizeSelector()}
                      </div> `
                    : nothing}
            </div>
        `;
    }

    protected renderPageSizeSelector() {
        const showPageSizeSelector = this.paginationOptions.some(
            option => parseInt(option) < this.totalItemCount
        );
        if (!showPageSizeSelector) {
            return nothing;
        }
        return html`<vaadin-select
            theme="small"
            aria-labelledby="page-size-label"
            style="width: 4.8rem; --vaadin-input-field-value-font-size: var(--lumo-font-size-s);"
            .items="${this.paginationOptions.map(it => ({
                label: it,
                value: it,
            }))}"
            .value="${this.pageSize.toString()}"
            @value-changed="${(e: CustomEvent) => {
                this.pageSize = parseInt(e.detail.value);
                this.updatePageCount();
                this.dispatchPageChanged();
            }}"
        ></vaadin-select>`;
    }

    protected updated(changedProperties: PropertyValues<this>) {
        if (changedProperties.has('totalItemCount')) {
            this.dispatchPageChanged();
        }
    }

    protected override willUpdate(changedProperties: PropertyValues<this>) {
        if (changedProperties.has('totalItemCount')) {
            this.updatePageCount();
        }
    }

    private updatePageCount() {
        const pageSize = clone(this.pageSize);
        const currentPage = clone(this.currentPage);
        if (this.totalItemCount === 0) {
            this.pageCount = 1; // we still want to display one page even though there are no items
        } else {
            this.pageCount = Math.ceil(this.totalItemCount / this.pageSize);
        }
        if (this.currentPage > this.pageCount) {
            this.currentPage = this.pageCount;
        }
        if (currentPage !== this.currentPage || pageSize !== this.pageSize) {
            this.dispatchPageChanged();
        }
    }

    private dispatchPageChanged() {
        this.dispatchEvent(
            new CustomEvent('page-changed', {
                detail: {
                    currentPage: this.currentPage,
                    pageSize: this.pageSize,
                },
                bubbles: true,
                composed: true,
            })
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'tm-pagination-controls': TmPaginationControls;
    }
}
